import React, { useState, useMemo, useEffect } from "react";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LabelWithControl } from "components/common/LabelControl";
import { showNotificationDialogManualOrder } from "redux/actions/notificationManualOrderAction";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import {
  addGoldenStoreData,
  getGoldenStoreSearchedData,
  resetAddGoldenStoreResult,
  resetSearchResult,
} from "redux/actions/golderStoreAction";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { SUPPLYCHAIN } from "components/common/CONSTANTS";
import { showErrorMessageByCode } from "services/commonServices";

export const ConfigureGoldenStore = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  const {
    searchGoldenStoresResponse,
    loading,
    isSearchSuccess,
    isSearchError,
    isUpdateStoreSuccess,
    isUpdateStoreError,
    addGoldenStoreResponse,
  } = useSelector((state) => state.goldenStore);

  const [storeAddedMessage, setStoreAddedMessage] = useState("");
  const [errorMessages, setErrorMessages] = useState([]);
  const [filteredPastEndDate] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [modifiedStores, setModifiedStores] = useState(new Set());
  const [page] = useState(1);
  const [pastEndDate] = useState("");
  const [popupStores, setPopupStores] = useState([]);
  const [rowsPerPage] = useState(500);
  const [searchedStore, setSearchedStores] = useState([]);
  const [searchIdTerm, setSearchIdTerm] = useState("");
  const [searchTerm] = useState("");
  const [selectedPastEndDate, setSelectedPastEndDate] = useState(null);
  const [selectedStores, setSelectedStores] = useState(new Set());
  const [showAlert, setShowAlert] = useState(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [validStoreIds, setValidStoreIds] = useState([]);
  const { locationResponse } = useSelector((state) => state.locations);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [validStores, setValidStores] = useState([]);
  const [storesToUpdate, setstoresToUpdate] = useState([]);
  const [storesToDelete, setstoresToDelete] = useState([]);

  useEffect(() => {
    const stores = [];
    locationResponse.forEach(function (location) {
      if (location.channel.toLowerCase() === SUPPLYCHAIN) {
        stores.push(location.locationId);
      }
    });
    setValidStores(stores);
  }, [locationResponse]);

  useEffect(() => {
    dispatch(getGoldenStoreSearchedData("", ""));
  }, [dispatch]);

  const handleSelectStore = (storeId) => {
    setSelectedStores((prev) => {
      const newSelected = new Set(prev);
      newSelected.has(storeId)
        ? newSelected.delete(storeId)
        : newSelected.add(storeId);
      return newSelected;
    });
  };

  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const confirmDelete = () => {
    const newDeletedStores = popupStores.map((store) => {
      return { ...store, isDelete: true };
    });
    const payload = {
      goldenStores: newDeletedStores,
    };
    dispatch(addGoldenStoreData(payload));

    const remainingSearchStores = searchedStore.filter(
      (store) => !selectedStores.has(store.storeId)
    );

    setSearchedStores(remainingSearchStores);

    setstoresToDelete(newDeletedStores);
    handleCloseDeleteDialog();
  };

  const handleDeleteStores = () => {
    const selectedStoreDetails = searchedStore.filter((store) =>
      selectedStores.has(store.storeId)
    );
    setPopupStores(selectedStoreDetails);
    handleOpenDeleteDialog();
  };

  const handleUpdateStores = () => {
    const updatedStores = searchedStore.filter(
      (store) =>
        modifiedStores.has(store.storeId) &&
        dayjs(store.startDate).isSameOrBefore(dayjs(store.endDate))
    );

    // Normalize dates to ensure correct UTC handling without "Z"
    const formattedStores = updatedStores.map((store) => {
      // Use Day.js to handle the start and end date
      let startDate = dayjs(store.startDate);
      let endDate = dayjs(store.endDate);

      // Convert start date to 00:00:00 (start of the day)
      startDate = startDate.startOf("day"); // This sets the time to 00:00:00
      const formattedStartDate = startDate.format("YYYY-MM-DD[T]HH:mm:ss"); // Format without Z

      // Convert end date to 23:59:59 (end of the day)
      endDate = endDate.endOf("day"); // This sets the time to 23:59:59
      const formattedEndDate = endDate.format("YYYY-MM-DD[T]HH:mm:ss"); // Format without Z

      return {
        ...store,
        startDate: formattedStartDate, // Store without Z
        endDate: formattedEndDate, // Store without Z
        isDelete: false,
      };
    });

    setstoresToUpdate(formattedStores);

    const payload = {
      goldenStores: formattedStores,
    };

    dispatch(addGoldenStoreData(payload));
  };

  useEffect(() => {
    if (isUpdateStoreSuccess) {
      let dialogTitle = "";
      let dialogText = "No Stores Updated";
      if (storesToUpdate.length > 0) {
        dialogTitle = "Updated Successfully";
        dialogText = `Stores Updated Successfully : ${storesToUpdate
          .map((store) => store.storeName)
          .join(", ")}`;
        setstoresToUpdate([]);
      } else if (storesToDelete.length > 0) {
        dialogTitle = "Deleted Successfully";
        dialogText = `Stores Deleted Successfully : ${storesToDelete
          .map((store) => store.storeName)
          .join(", ")}`;
        setstoresToDelete([]);
        setSelectedStores(new Set());
      }

      dispatch(
        showNotificationDialogManualOrder(dialogTitle, dialogText, "Done")
      );
      dispatch(getGoldenStoreSearchedData("", ""));
      setModifiedStores(new Set());
      dispatch(resetAddGoldenStoreResult());
    } else if (isUpdateStoreError) {
      const { errTitle, errContent } = showErrorMessageByCode(
        addGoldenStoreResponse
      );
      dispatch(showNotificationDialogManualOrder(errTitle, errContent, "Done"));
      dispatch(getGoldenStoreSearchedData("", ""));
      dispatch(resetAddGoldenStoreResult());
    }
  }, [
    addGoldenStoreResponse,
    dispatch,
    isUpdateStoreError,
    isUpdateStoreSuccess,
    modifiedStores,
    storesToUpdate,
    storesToDelete,
  ]);

  const handleClose = () => navigate("/");

  const handleDateChange = (storeId, date, isStartDate) => {
    const store = searchedStore.find((s) => s.storeId === storeId);
    if (!store) return;

    const parsedDate = dayjs(date);
    const today = dayjs();

    if (
      !isStartDate &&
      dayjs(store.endDate).isBefore(today) &&
      dayjs(store.startDate).isBefore(today)
    ) {
      handleDateChange(storeId, today, true);
    }

    // Update store date correctly
    setSearchedStores((prev) =>
      prev.map((s) =>
        s.storeId === storeId
          ? {
              ...s,
              [isStartDate ? "startDate" : "endDate"]:
                parsedDate.format("YYYY-MM-DD"),
            }
          : s
      )
    );

    //  Ensure the update button stays enabled
    setModifiedStores((prev) => {
      const updatedSet = new Set(prev);
      updatedSet.add(storeId);
      return updatedSet;
    });
  };

  const isRowValid = (store) => {
    if (!store || !store.startDate || !store.endDate) return false;

    // Convert dates properly before comparing
    const start = dayjs(store.startDate).startOf("day");
    const end = dayjs(store.endDate).startOf("day");
    const today = dayjs().startOf("day");

    // Perform all validations
    const isStartValid = start.isSameOrAfter(today, "day");
    const isEndValid = end.isSameOrAfter(today, "day");
    const isEndAfterStart = end.isSameOrAfter(start, "day");

    return isStartValid && isEndValid && isEndAfterStart;
  };
  const areAllRowsValid = () => searchedStore.some(isRowValid);

  const areModifiedStoresValid = () => {
    return Array.from(modifiedStores).every((storeId) => {
      const store = searchedStore.map((s) => s.storeId === storeId);
      return dayjs(store.endDate).isSameOrAfter(dayjs(store.startDate));
    });
  };

  const filteredStores = useMemo(() => {
    const idSearchTerms = searchIdTerm
      .split(",")
      .map((storeId) => storeId.trim())
      .filter(Boolean);

    return searchedStore.filter((store) => {
      // Ensure store.storeId is defined and convert it to a string
      const storeId = store.storeId ? store.storeId.toString() : null;

      // Ensure store.storeName is defined and use .toLowerCase() safely
      const storeName = store.storeName ? store.storeName.toLowerCase() : "";

      const idMatches =
        !idSearchTerms?.length || (storeId && idSearchTerms.includes(storeId)); // Only match if storeId is valid

      const endDateMatches =
        !pastEndDate ||
        dayjs(store.endDate).isSameOrBefore(dayjs(pastEndDate), "day");

      return (
        storeName.includes(searchTerm.toLowerCase()) &&
        idMatches &&
        endDateMatches
      );
    });
  }, [searchTerm, searchIdTerm, pastEndDate, searchedStore]);

  const sortedStores = useMemo(() => {
    let filteredStores = searchedStore;

    // fitlering based on pastEndDate
    if (filteredPastEndDate) {
      filteredStores = filteredStores.filter((store) => {
        const storeEndDate = new Date(store.endDate);
        return (
          storeEndDate <=
          new Date(filteredPastEndDate).setHours(23, 59, 59, 999)
        );
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredStores = [...filteredStores].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }

    return filteredStores;
  }, [sortConfig, filteredPastEndDate, searchedStore]);

  const handleSearchIdChange = (value) => {
    setSearchIdTerm(value);
  };

  const handleAddStore = () => {
    if (validStoreIds.length) {
      // Using Day.js to get the current date
      const now = dayjs();

      // Set the start date to 00:00:00 (start of the day)
      const startDate = now.startOf("day"); // This sets the time to 00:00:00
      const formattedStartDate = startDate.format("YYYY-MM-DD[T]HH:mm:ss"); // Format the date without "Z"

      // Set the end date to 23:59:59 (end of the day)
      const endDate = now.endOf("day"); // This sets the time to 23:59:59
      const formattedEndDate = endDate.format("YYYY-MM-DD[T]HH:mm:ss"); // Format the date without "Z"
      const newStores = [];

      const filteredValidStores = locationResponse.filter((x) =>
        validStoreIds.includes(x.locationId)
      );

      filteredValidStores.forEach((storeData) => {
        newStores.push({
          storeId: storeData.locationId,
          storeName: storeData.locationName,
          startDate: formattedStartDate, // Store without Z
          endDate: formattedEndDate, // Store without Z
          isDelete: false,
        });
      });

      if (newStores.length > 0) {
        setErrorMessages("");
        setSearchedStores((prev) => [...newStores, ...prev]);

        setModifiedStores((prevModifiedStores) => {
          const updatedModifiedStores = new Set(prevModifiedStores);
          newStores.forEach((store) =>
            updatedModifiedStores.add(store.storeId)
          );
          return updatedModifiedStores;
        });
        setStoreAddedMessage(
          `Stores added successfully - ${newStores
            .map((store) => store.storeName)
            .join(", ")}`
        );
      } else {
        setErrorMessages([
          `Incorrect Store ID (${validStoreIds.join(
            ", "
          )}), please enter correct core Store ID.`,
        ]);
        setAddButtonDisabled(true);
      }

      // Reset input fields
      setSearchIdTerm("");
      setValidStoreIds([]);
      setAddButtonDisabled(true);
    }
  };
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  const handleSearch = () => {
    setModifiedStores(new Set());
    setStoreAddedMessage("");
    setAddButtonDisabled(true);
    setSearchedStores([]);

    dispatch(
      getGoldenStoreSearchedData(
        searchIdTerm,
        selectedPastEndDate === null ? "" : selectedPastEndDate
      )
    );
  };

  useEffect(() => {
    // setSearchedStores(searchGoldenStoresResponse.goldenStores);

    const invalidStores = [];
    const existingStores = [];
    const missingStores = [];
    const errors = [];

    if (
      isSearchSuccess ||
      (isSearchError && searchGoldenStoresResponse.response.status === 404)
    ) {
      // Split and trim store IDs entered by the user
      const enteredIds = searchIdTerm
        .split(",")
        .map((storeId) => Number(storeId.trim()))
        .filter(Boolean);

      const searchedGoldenStores = searchGoldenStoresResponse.goldenStores
        ? searchGoldenStoresResponse.goldenStores
        : [];

      const alreadyAddedStores = searchedGoldenStores.map(
        (store) => store.storeId
      );

      enteredIds.forEach(function (store) {
        if (alreadyAddedStores.includes(store)) {
          existingStores.push(store);
        } else if (validStores.includes(store)) {
          missingStores.push(store);
        } else {
          invalidStores.push(store);
        }
      });

      // Dispatch action to fetch data (based on search criteria)
      if (existingStores.length > 0) {
        const storeList = searchedGoldenStores.filter((x) =>
          existingStores.includes(x.storeId)
        );
        setSearchedStores(storeList);
      } else if (searchIdTerm.length <= 0) {
        setSearchedStores(searchedGoldenStores);
      }
      // Handle invalid store IDs (if any)
      if (invalidStores.length > 0) {
        errors.push(
          `Incorrect Store ID (${invalidStores.join(
            ", "
          )}), please enter correct core Store ID.`
        );
        setAddButtonDisabled(true); // Disable Add button for invalid stores
      }

      if (missingStores.length > 0 && !selectedPastEndDate) {
        setValidStoreIds(missingStores);
        // Handle stores that are valid but not found in the list
        errors.push(
          `Store id not found in the golden store list (${missingStores.join(
            ", "
          )}). Please click on the Add button to add it to golden store list. `
        );
        setAddButtonDisabled(false); // Enable Add button for stores not found
      }

      setErrorMessages(errors);

      dispatch(resetSearchResult());
    } else if (isSearchError) {
      const { errTitle, errContent } = showErrorMessageByCode(
        searchGoldenStoresResponse
      );
      dispatch(showNotificationDialogManualOrder(errTitle, errContent, "Done"));
      dispatch(resetSearchResult());
    }
  }, [
    searchGoldenStoresResponse,
    dispatch,
    isSearchError,
    isSearchSuccess,
    searchIdTerm,
    validStores,
    selectedPastEndDate,
  ]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all stores
      const allStoreIds = new Set(sortedStores.map((store) => store.storeId));
      setSelectedStores(allStoreIds);
    } else {
      // Deselect all stores
      setSelectedStores(new Set());
    }
  };

  const totalPages = Math.ceil(filteredStores?.length / rowsPerPage);

  return (
    <div
      className="mainPageStyling"
      style={{
        maxWidth: "700px",
        margin: "0 auto",
      }}
    >
      <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
        Golden Store
      </Typography>
      <div
        style={{
          display: "flex",
          marginBottom: "4px",
          marginTop: "6px",
          justifyContent: "space-between",
        }}
      >
        <div className="labelControl">
          <Grid
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <Grid container spacing={12} sx={{ "& > *": { flex: 1 } }}>
              <Grid item xs={4} sm={4} md={4}>
                <LabelWithControl label="Store ID">
                  <TextField
                    placeholder="Search Store ID"
                    variant="outlined"
                    value={searchIdTerm}
                    fullWidth
                    onChange={(e) =>
                      handleSearchIdChange(
                        e.target.value.replace(/[^0-9, ]/g, "")
                      )
                    }
                  />
                </LabelWithControl>
              </Grid>
              <Grid item xs={4} sm={4} md={4}>
                <div>
                  <LabelWithControl label="Past End Date">
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={
                        selectedPastEndDate ? dayjs(selectedPastEndDate) : null
                      }
                      onChange={(newValue) => {
                        if (newValue) {
                          const today = dayjs().startOf("day"); // Get today's date
                          if (newValue.isAfter(today)) {
                            setShowAlert(true); // Show warning alert
                            setSelectedPastEndDate(null); // Reset the date value
                          } else {
                            setShowAlert(false); // Hide warning alert
                            const formattedDate = newValue.format("YYYY-MM-DD");
                            setSelectedPastEndDate(formattedDate);
                          }
                        } else {
                          setShowAlert(false); // Hide warning alert
                          setSelectedPastEndDate(null);
                        }
                      }}
                      variant="outlined"
                      disableFuture
                      slotProps={{
                        textField: {        
                          inputProps: { 'data-testid': 'past-end-date-input' },
                        },
                      }}
                    />
                  </LabelWithControl>
                </div>
              </Grid>

              <Grid item xs={4} sm={4} md={4}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "end",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    style={{
                      height: "25px",
                      fontSize: "12px",
                      marginTop: "16px",
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAddStore}
                    style={{
                      marginLeft: "8px",
                      height: "25px",
                      fontSize: "12px",
                      marginTop: "16px",
                    }}
                    disabled={addButtonDisabled}
                  >
                    Add +
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </div>
      {errorMessages.length > 0 ? (
        <>
          {errorMessages.map((message) => (
            <Typography
              key={message}
              color="error"
              variant="body2"
              style={{ marginTop: "0px", marginBottom: "8px" }}
            >
              {message}
            </Typography>
          ))}
        </>
      ) : null}
      {storeAddedMessage && (
        <Typography
          color="success"
          variant="body2"
          style={{ marginTop: "0px", marginBottom: "8px" }}
        >
          {storeAddedMessage}
        </Typography>
      )}
      {!areAllRowsValid() && searchedStore.length > 0 && (
        <Alert
          severity="warning"
          style={{
            height: "38px",
            padding: "1px",
            marginBottom: "6px",
            width: "auto",
          }}
        >
          Unable to update any store because of date validation issue!
        </Alert>
      )}
      {showAlert && (
        <Alert
          severity="warning"
          style={{
            height: "38px",
            padding: "1px",
            marginBottom: "6px",
            width: "auto",
          }}
        >
          Past End Date cannot be Future Date.
        </Alert>
      )}
      <TableContainer component={Paper} className="tableStructure tableStyle">
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#004E37",
                fontWeight: "bold",
              }}
            >
              <TableCell>
                <Tooltip title="Select all rows for bulk delete" arrow>
                  <Checkbox
                    checked={
                      selectedStores.size === sortedStores.length &&
                      sortedStores.length > 0
                    }
                    onChange={handleSelectAll}
                    className="checkboxStyle"
                    sx={{
                      color: "white",
                      "&.MuiCheckbox-root": { borderColor: "white" },
                      "&.Mui-checked": { color: "white" },
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  color: "white",
                  textAlign: "right",
                }}
              >
                <TableSortLabel
                  className="sortHandle"
                  active={sortConfig.key === "storeId"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("storeId")}
                  IconComponent={
                    sortConfig.direction === "ascending"
                      ? ArrowUpwardIcon
                      : ArrowDownwardIcon
                  }
                  sx={{
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "& .MuiTableSortLabel-icon": {
                      color: "white !important",
                    },
                    "&.MuiTableSortLabel-active": {
                      color: "white !important",
                    },
                  }}
                >
                  Store ID
                </TableSortLabel>
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  color: "white",
                  textAlign: "center",
                }}
              >
                <TableSortLabel
                  className="sortHandle"
                  active={sortConfig.key === "storeName"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("storeName")}
                  IconComponent={
                    sortConfig.direction === "ascending"
                      ? ArrowUpwardIcon
                      : ArrowDownwardIcon
                  }
                  sx={{
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "& .MuiTableSortLabel-icon": {
                      color: "white !important",
                    },
                    "&.MuiTableSortLabel-active": {
                      color: "white !important",
                    },
                  }}
                >
                  Store Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  color: "white",
                  textAlign: "center",
                }}
              >
                <TableSortLabel
                  className="sortHandle"
                  active={sortConfig.key === "startDate"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("startDate")}
                  IconComponent={
                    sortConfig.direction === "ascending"
                      ? ArrowUpwardIcon
                      : ArrowDownwardIcon
                  }
                  sx={{
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "& .MuiTableSortLabel-icon": {
                      color: "white !important",
                    },
                    "&.MuiTableSortLabel-active": {
                      color: "white !important",
                    },
                  }}
                >
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                style={{
                  fontWeight: "bold",
                  color: "white",
                  textAlign: "center",
                }}
              >
                <TableSortLabel
                  className="sortHandle"
                  active={sortConfig.key === "endDate"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("endDate")}
                  IconComponent={
                    sortConfig.direction === "ascending"
                      ? ArrowUpwardIcon
                      : ArrowDownwardIcon
                  }
                  sx={{
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "& .MuiTableSortLabel-icon": {
                      color: "white !important",
                    },
                    "&.MuiTableSortLabel-active": {
                      color: "white !important",
                    },
                  }}
                >
                  End Date
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={14}
                  style={{
                    textAlign: "center",
                    position: "relative",
                    height: "100px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 12,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : sortedStores?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} style={{ textAlign: "center" }}>
                  <Typography style={{ fontSize: "12px" }}>
                    No matching records found! Please check your search
                    criteria.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedStores
                .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                .map((store) => (
                  <TableRow key={store.storeId} style={{ textAlign: "center" }}>
                    <TableCell>
                      <Checkbox
                        checked={selectedStores.has(store.storeId)}
                        onChange={() => handleSelectStore(store.storeId)}
                        className="checkboxStyle"
                      />
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {store.storeId}
                    </TableCell>
                    <TableCell>{store.storeName}</TableCell>

                    <TableCell style={{ textAlign: "center" }}>
                      <span style={{ display: "flex" }}>
                        <DatePicker
                          className="inputBox inputGoldenStore"
                          format="DD/MM/YYYY"
                          value={dayjs(store.startDate)}
                          minDate={dayjs()} // Ensures the DatePicker doesn't allow selecting past dates
                          onChange={(newValue) =>
                            handleDateChange(store.storeId, newValue, true)
                          }
                          slotProps={{
                            textField: {        
                              inputProps: { 'data-testid': 'start-date-input' },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              style={{ maxWidth: "250px" }}
                            />
                          )}
                        />
                        {/* Check if startDate is invalid */}
                        {!dayjs(store.startDate).isSameOrAfter(
                          dayjs(),
                          "day"
                        ) && (
                          <Tooltip
                            title={"Please select current or future date"}
                            arrow
                            placement="bottom"
                          >
                            <IconButton
                              sx={{
                                color: "rgb(237, 108, 2)",
                                marginLeft: "auto",
                                padding: "0",
                              }}
                            >
                              <ErrorOutlineIcon
                                sx={{ fontSize: "18px", display: "flex" }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </span>
                    </TableCell>

                    <TableCell style={{ textAlign: "center" }}>
                      <span style={{ display: "flex" }}>
                        <DatePicker
                          className="inputBox inputGoldenStore"
                          format="DD/MM/YYYY"
                          value={dayjs(store.endDate)}
                          minDate={dayjs(store.startDate)}
                          onChange={(newValue) => {
                            handleDateChange(store.storeId, newValue, false);
                          }}
                          slotProps={{
                            textField: {        
                              inputProps: { 'data-testid': 'end-date-input' },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              style={{ maxWidth: "250px" }}
                              error={dayjs(store.endDate).isBefore(
                                dayjs(store.startDate),
                                "day"
                              )}
                            />
                          )}
                        />
                        {dayjs(store.endDate).isBefore(
                          dayjs(store.startDate)
                        ) && (
                          <Tooltip
                            title={"End date can't be earlier than Start date"}
                            arrow
                            placement="bottom"
                          >
                            <IconButton
                              sx={{
                                color: "red",
                                marginLeft: "auto",
                                padding: "0",
                              }}
                            >
                              <ErrorOutlineIcon sx={{ fontSize: "18px" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            marginTop: 8,
          }}
        ></div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 16,
          marginBottom: 16,
        }}
      >
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteStores}
          disabled={selectedStores.size === 0}
          style={{ height: "25px", fontSize: "12px" }}
        >
          Delete
        </Button>
        <Dialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="sm"
          fullWidth={true}
        >
          <DialogTitle
            style={{
              fontWeight: "bold",
              fontSize: "14px",
              margin: "0 20px",
              padding: "10px 0",
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
            }}
          >
            Confirm Delete
          </DialogTitle>
          <DialogContent style={{ paddingTop: "10px", fontSize: "12px" }}>
            Please confirm, do you want to delete the stores listed below?
            <Typography
              style={{
                marginTop: "10px",
                fontWeight: "bold",
                fontSize: "12px",
              }}
            >
              Store Details:
            </Typography>
            <Table
              sx={{ borderLeft: "1px solid rgba(224, 224, 224, 1)" }}
              className="confirmDeletePopup"
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#004E37", color: "white" }}>
                  <TableCell sx={{ color: "white" }}>
                    <strong>Store ID</strong>
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>
                    <strong>Store Name</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {popupStores.map((store) => (
                  <TableRow key={store.storeId}>
                    <TableCell>{store.storeId}</TableCell>
                    <TableCell>{store.storeName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions className="confirmDeleteButtons">
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="primary" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateStores}
            style={{ marginRight: 8, height: "25px", fontSize: "12px" }}
            // disabled={modifiedStores.size === 0 || !areAllRowsValid()}
            disabled={modifiedStores.size === 0 || !areModifiedStoresValid()}
          >
            Update
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleClose}
            style={{ height: "25px", fontSize: "12px" }}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};
